import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./components/Layout";
import { RequireAuth } from "./components/RequireAuth";
import { ForcePasswordChange } from "./components/ForcePasswordChange";

// Sprint Y-5 : code-splitting par route. Le dashboard et la login restent
// eager-loaded (chemins critiques) ; tout le reste est lazy → chaque page
// devient son propre chunk JS, charge a la navigation.
import { DashboardPage } from "./pages/DashboardPage";
import { LoginPage } from "./pages/LoginPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";

const TenantsPage = lazy(() =>
  import("./pages/TenantsPage").then((m) => ({ default: m.TenantsPage })),
);
const ExhibitionsPage = lazy(() =>
  import("./pages/ExhibitionsPage").then((m) => ({ default: m.ExhibitionsPage })),
);
const ParcoursPage = lazy(() =>
  import("./pages/ParcoursPage").then((m) => ({ default: m.ParcoursPage })),
);
const ParcoursDetailPage = lazy(() =>
  import("./pages/ParcoursDetailPage").then((m) => ({ default: m.ParcoursDetailPage })),
);
const POIsPage = lazy(() =>
  import("./pages/POIsPage").then((m) => ({ default: m.POIsPage })),
);
const POIDetailPage = lazy(() =>
  import("./pages/POIDetailPage").then((m) => ({ default: m.POIDetailPage })),
);
const MediaPage = lazy(() =>
  import("./pages/MediaPage").then((m) => ({ default: m.MediaPage })),
);
const ImportPage = lazy(() =>
  import("./pages/ImportPage").then((m) => ({ default: m.ImportPage })),
);
const PartnersPage = lazy(() =>
  import("./pages/PartnersPage").then((m) => ({ default: m.PartnersPage })),
);
const AuditPage = lazy(() =>
  import("./pages/AuditPage").then((m) => ({ default: m.AuditPage })),
);
const UsersPage = lazy(() =>
  import("./pages/UsersPage").then((m) => ({ default: m.UsersPage })),
);
// 02/11/2026 : les droits d'UNE PERSONNE, par-dessus son rôle.
const UserRightsPage = lazy(() =>
  import("./pages/UserRightsPage").then((m) => ({ default: m.UserRightsPage })),
);
const PurchasesPage = lazy(() =>
  import("./pages/PurchasesPage").then((m) => ({ default: m.PurchasesPage })),
);
// Codes comptoir (V1.33)
const CounterCodesPage = lazy(() =>
  import("./pages/CounterCodesPage").then((m) => ({ default: m.CounterCodesPage })),
);
const CounterBatchPage = lazy(() =>
  import("./pages/CounterBatchPage").then((m) => ({ default: m.CounterBatchPage })),
);
const DemoAccessPage = lazy(() =>
  import("./pages/DemoAccessPage").then((m) => ({ default: m.DemoAccessPage })),
);
const FaqsPage = lazy(() =>
  import("./pages/FaqsPage").then((m) => ({ default: m.FaqsPage })),
);
// Contenus › Infos visiteur (V1.28) : FAQ visiteur et infos pratiques par établissement.
const VisitorInfoPage = lazy(() =>
  import("./pages/VisitorInfoPage").then((m) => ({ default: m.VisitorInfoPage })),
);
const GuidePage = lazy(() =>
  import("./pages/GuidePage").then((m) => ({ default: m.GuidePage })),
);
const CollectionsPage = lazy(() =>
  import("./pages/CollectionsPage").then((m) => ({ default: m.CollectionsPage })),
);
const RoomsPage = lazy(() =>
  import("./pages/RoomsPage").then((m) => ({ default: m.RoomsPage })),
);
const TmsPage = lazy(() => import("./pages/TmsPage").then((m) => ({ default: m.TmsPage })));
const SlideshowsPage = lazy(() =>
  import("./pages/SlideshowsPage").then((m) => ({ default: m.SlideshowsPage })),
);
const EstablishmentsPage = lazy(() =>
  import("./pages/EstablishmentsPage").then((m) => ({ default: m.EstablishmentsPage })),
);
const MiniGamesPage = lazy(() =>
  import("./pages/MiniGamesPage").then((m) => ({ default: m.MiniGamesPage })),
);
const TreasureHuntsPage = lazy(() =>
  import("./pages/TreasureHuntsPage").then((m) => ({ default: m.TreasureHuntsPage })),
);
const TreasureHuntDetailPage = lazy(() =>
  import("./pages/TreasureHuntDetailPage").then((m) => ({ default: m.TreasureHuntDetailPage })),
);
const PwaProjectsPage = lazy(() =>
  import("./pages/PwaProjectsPage").then((m) => ({ default: m.PwaProjectsPage })),
);
const PwaProjectDetailPage = lazy(() =>
  import("./pages/PwaProjectDetailPage").then((m) => ({ default: m.PwaProjectDetailPage })),
);
// Sprint CQ-2 : page Statistiques
// Reporting › Visiteurs (V1.15) : remplace l'ancienne page Statistiques.
const ReportingVisitorsPage = lazy(() =>
  import("./pages/ReportingVisitorsPage").then((m) => ({ default: m.ReportingVisitorsPage })),
);
// Reporting › Qualité (V1.23) : incidents de la PWA visiteur.
const NdpGroupsPage = lazy(() => import("./pages/NdpGroupsPage").then((m) => ({ default: m.NdpGroupsPage })));
const ReportingQualityPage = lazy(() =>
  import("./pages/ReportingQualityPage").then((m) => ({ default: m.ReportingQualityPage })),
);
// Reporting › Billetterie (V1.13)
const ReportingTicketsPage = lazy(() =>
  import("./pages/ReportingTicketsPage").then((m) => ({ default: m.ReportingTicketsPage })),
);
// Reporting › Royautés (V1.14)
const ReportingRoyaltiesPage = lazy(() =>
  import("./pages/ReportingRoyaltiesPage").then((m) => ({ default: m.ReportingRoyaltiesPage })),
);
// Sprint Droits Lot E : « Mon compte »
const AccountPage = lazy(() =>
  import("./pages/AccountPage").then((m) => ({ default: m.AccountPage })),
);

function RouteFallback() {
  return (
    <div className="p-8 text-sm text-[color:var(--color-alto-muted)]">Chargement…</div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset" element={<ResetPasswordPage />} />

      <Route
        element={
          <RequireAuth>
            <ForcePasswordChange>
              <Layout />
            </ForcePasswordChange>
          </RequireAuth>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route
          path="/account"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AccountPage />
            </Suspense>
          }
        />
        <Route
          path="/tenants"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TenantsPage />
            </Suspense>
          }
        />
        <Route
          path="/collections"
          element={
            <Suspense fallback={<RouteFallback />}>
              <CollectionsPage />
            </Suspense>
          }
        />
        {/* 28/10/2026 : l'outil IA est devenu un onglet de « Diaporamas et time
            codes ». L'ancienne adresse y mène. */}
        <Route path="/timecode" element={<Navigate to="/slideshows" replace />} />
        <Route
          path="/slideshows"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SlideshowsPage />
            </Suspense>
          }
        />
        <Route
          path="/rooms"
          element={
            <Suspense fallback={<RouteFallback />}>
              <RoomsPage />
            </Suspense>
          }
        />
        <Route
          path="/tms"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TmsPage />
            </Suspense>
          }
        />
        <Route
          path="/establishments"
          element={
            <Suspense fallback={<RouteFallback />}>
              <EstablishmentsPage />
            </Suspense>
          }
        />
        <Route
          path="/mini-games"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MiniGamesPage />
            </Suspense>
          }
        />
        <Route
          path="/treasure-hunts"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TreasureHuntsPage />
            </Suspense>
          }
        />
        <Route
          path="/treasure-hunts/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TreasureHuntDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/pwa-projects"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PwaProjectsPage kind="STANDARD" />
            </Suspense>
          }
        />
        <Route
          path="/pwa-projects-mono"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PwaProjectsPage kind="MONO" />
            </Suspense>
          }
        />
        <Route
          path="/pwa-projects/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PwaProjectDetailPage />
            </Suspense>
          }
        />
        {/* Point 4 : detail d'une PWA - Mono sous un segment distinct pour que
            le menu de gauche garde « Projets PWA - Mono » actif. Meme page. */}
        <Route
          path="/pwa-projects-mono/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PwaProjectDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/exhibitions"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ExhibitionsPage />
            </Suspense>
          }
        />
        <Route
          path="/parcours"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ParcoursPage />
            </Suspense>
          }
        />
        <Route
          path="/parcours/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ParcoursDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/pois"
          element={
            <Suspense fallback={<RouteFallback />}>
              <POIsPage />
            </Suspense>
          }
        />
        <Route
          path="/pois/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <POIDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/media"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MediaPage />
            </Suspense>
          }
        />
        <Route
          path="/import"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ImportPage />
            </Suspense>
          }
        />
        <Route
          path="/partners"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PartnersPage />
            </Suspense>
          }
        />
        <Route
          path="/audit"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AuditPage />
            </Suspense>
          }
        />
        <Route
          path="/users"
          element={
            <Suspense fallback={<RouteFallback />}>
              <UsersPage />
            </Suspense>
          }
        />
        <Route
          path="/droits-par-personne"
          element={
            <Suspense fallback={<RouteFallback />}>
              <UserRightsPage />
            </Suspense>
          }
        />
        <Route
          path="/demo-access"
          element={
            <Suspense fallback={<RouteFallback />}>
              <DemoAccessPage />
            </Suspense>
          }
        />
        <Route
          path="/faqs"
          element={
            <Suspense fallback={<RouteFallback />}>
              <FaqsPage />
            </Suspense>
          }
        />
        <Route
          path="/visitor-info"
          element={
            <Suspense fallback={<RouteFallback />}>
              <VisitorInfoPage />
            </Suspense>
          }
        />
        <Route
          path="/guide"
          element={
            <Suspense fallback={<RouteFallback />}>
              <GuidePage />
            </Suspense>
          }
        />
        <Route
          path="/sav/purchases"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PurchasesPage />
            </Suspense>
          }
        />
        <Route
          path="/counter-codes"
          element={
            <Suspense fallback={<RouteFallback />}>
              <CounterCodesPage />
            </Suspense>
          }
        />
        <Route
          path="/counter-codes/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <CounterBatchPage />
            </Suspense>
          }
        />
        {/* Sprint CQ-2 : page Statistiques avec filtres + Recharts */}
        <Route
          path="/stats"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportingVisitorsPage />
            </Suspense>
          }
        />
        <Route
          path="/reporting/visiteurs"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportingVisitorsPage />
            </Suspense>
          }
        />
        <Route
          path="/groupes-notre-dame"
          element={
            <Suspense fallback={<RouteFallback />}>
              <NdpGroupsPage />
            </Suspense>
          }
        />
        <Route
          path="/reporting/qualite"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportingQualityPage />
            </Suspense>
          }
        />
        <Route
          path="/reporting/billetterie"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportingTicketsPage />
            </Suspense>
          }
        />
        <Route
          path="/reporting/redevance"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportingRoyaltiesPage />
            </Suspense>
          }
        />
        {/* V1.37 : « Royautés » devient « Redevance » ; l'ancienne adresse reste servie (signets). */}
        <Route path="/reporting/royautes" element={<Navigate to="/reporting/redevance" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
